/**
 * `/campaigns/:campaignId` — one campaign, and the truth about what it did.
 *
 * The screen is arranged around the three numbers that can mislead somebody, in
 * the order they mislead them:
 *
 *   1. **the reach is the channel reach.** 526 people, not the 1,278 in the
 *      segment. Slice 2 settled it against real data and this is the screen
 *      where the number stands next to a Schedule button;
 *   2. **the deliverable count sits beside it.** The send guard only delivers to
 *      allow-listed addresses, so "526 people" and "1 delivery" are both true in
 *      this environment. Showing the first alone is how somebody concludes the
 *      send is broken;
 *   3. **every skip has a reason with a sentence.** "1,277 skipped" is not an
 *      explanation. Broken down by reason, with each reason's own sentence and a
 *      link to exactly which people it is about, it is.
 *
 * While the campaign can still move the screen polls the API and re-reads all of
 * it. Nothing is incremented locally and nothing is predicted: every figure here
 * came back from the request that drew it. A campaign that has finished is not
 * polled at all, and one scheduled for next week is left alone until its time
 * approaches — see `use-campaign-progress.ts`.
 *
 * There is no delete. `wend_app` holds no DELETE on `campaigns`, because a
 * campaign that has sent anything is a record of messages that reached real
 * people; cancelling is how one goes away, and it settles everybody still
 * waiting rather than leaving them pending for ever.
 *
 * ## What slice 17 took off the screen, and where it went
 *
 * Three card descriptions on this page were explaining the *implementation* to
 * whoever picked up the next slice, and the campaign screen is the last place
 * that reader exists — somebody is here because a campaign to real people is
 * about to run, or has run.
 *
 * **The progress card's description is gone entirely.** It said the counts are
 * computed from the recipient rows on every read rather than from a stored
 * total, so a redelivered job or a crash mid-send cannot leave them describing
 * something that did not happen. That is true, it is the reason the API is
 * written the way it is, and **it is an argument for trusting the numbers rather
 * than a fact about this campaign** — a reader who never saw it draws no false
 * conclusion, which is precisely the test. It survives in
 * `campaign-progress.ts` on the API and in `CampaignProgressPanel`'s own header.
 * What replaces it is nothing: `WatchState` in the same card header already says
 * whether the figures are being refreshed, which is the only thing about their
 * provenance a person on this screen can act on.
 *
 * **The reach card's description became the sentence the rule actually asks
 * for.** It used to explain *why* a campaign does not touch the people it cannot
 * reach on its channel. It now says what the number is — the distinct addresses
 * on this channel, never the size of the segment — from `REACH_ADDRESS_PLURAL`,
 * because a person misreading reach as the segment size overstates their
 * audience at the moment they are deciding whether to send to it. The why is in
 * `CampaignReach.tsx`'s header, where it was already written down.
 *
 * **The cancel card lost its reasoning and gained a correction.** Its live-campaign
 * description named *Amazon* unconditionally — a WhatsApp campaign was told
 * cancelling "cannot recall a message Amazon has already accepted", about
 * messages Amazon never saw, in a product where being sent to the wrong console
 * costs an afternoon. It reads `CHANNEL_PROVIDER_SHORT_LABEL` now, the same
 * table the results panel writes its sentences from. Both descriptions and the
 * confirmation say outright that a cancellation cannot be undone, which is the
 * one thing this card must never leave to inference: cancelling settles everybody
 * still waiting as skipped, and a second click does not bring them back.
 *
 * ## Slice 18 added a fourth number that can mislead, and put it first
 *
 * **A campaign that has stopped, and a campaign that is waiting, look identical
 * in the counts.** Both sit at 102 of 1,278 with nothing moving. One continues
 * on its own in twenty minutes and one never continues until somebody resumes
 * it, and the difference is the whole of slice 18 — so `CampaignListSafetyCard`
 * sits **above** Progress rather than below it. A person who reads the counts
 * first and the explanation second has already formed the belief that the send
 * is broken, which is the same mistake the reach and deliverable numbers were
 * ordered to prevent three paragraphs above.
 *
 * The two statuses are what carry that difference, and they carry it in the
 * badge and in the header sentence rather than in a colour: `holding_between_waves`
 * says it continues on its own, `halted_delivery_risk` says it waits for a
 * person. `CAMPAIGN_STATUS_DISPLAY` takes both sentences from `@wend/shared`, so
 * the card below and the description here cannot come to disagree about which
 * one a campaign is in.
 *
 * **The cancel card stays visible on both of them.** `isCampaignCancellable`
 * includes them deliberately: a halt that could only ever be resumed would leave
 * several hundred recipients `pending` for ever with no way to settle them, and
 * the campaign somebody is most likely to want to abandon is exactly the one
 * that stopped because its list is bouncing.
 */
import {
  isCampaignCancellable,
  isCampaignTerminal,
  type Campaign,
  type CampaignScheduleReadiness,
} from '@wend/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Kicker,
  Spinner,
  buttonVariants,
  useToast,
} from '@wend/ui';
import { ArrowLeft, Ban, ListFilter, RotateCw } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { CampaignReachPanel } from '../../components/CampaignReach';
import { CampaignStatusBadge } from '../../components/CampaignStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { SendGuardNotice } from '../../components/SendGuardNotice';
import { TemplateStatusBadge } from '../../components/TemplateStatusBadge';
import {
  cancelCampaign,
  fetchCampaign,
  type CampaignWithReadinessResponse,
} from '../../lib/api/campaigns';
import { errorMessage, isApiError } from '../../lib/api/errors';
import {
  campaignSendingMeaning,
  CHANNEL_PROVIDER_SHORT_LABEL,
  formatInTimeZone,
} from '../../lib/campaign-display';
import { formatDateTime, pluralise } from '../../lib/format';
import { channelSurfaceLabel } from '../../lib/surface-display';
import { useAsyncData } from '../../lib/use-async-data';
import { useCampaignProgress, type CampaignWatch } from '../../lib/use-campaign-progress';
import { CampaignHoldoutCard } from './CampaignHoldoutCard';
import { CampaignListSafetyCard } from './CampaignListSafetyCard';
import { CampaignProgressPanel } from './CampaignProgressPanel';
import {
  CampaignRecipients,
  NO_RECIPIENT_FILTER,
  type RecipientFilter,
} from './CampaignRecipients';
import { CampaignResultsPanel } from './CampaignResultsPanel';
import { CampaignWhatsAppPersonalisation } from './CampaignWhatsAppPersonalisation';
import { REACH_ADDRESS_PLURAL } from './reach-words';
import { ScheduleCampaignPanel } from './ScheduleCampaignPanel';

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();

  // The route always supplies it; this is the type system's question, not a
  // state the app can reach, so it goes back to the list rather than erroring.
  if (campaignId === undefined) return <Navigate to="/campaigns" replace />;

  return <CampaignDetail campaignId={campaignId} />;
}

function CampaignDetail({ campaignId }: { campaignId: string }) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchCampaign(campaignId, signal),
    [campaignId],
  );

  /**
   * The campaign as the poll and this screen's own actions have moved it on
   * since the initial load. Every action answers with the whole campaign, so
   * nothing here has to guess what its own write did to the numbers. Cleared
   * whenever a fresh copy arrives, so the server always wins in the end.
   */
  const [live, setLive] = useState<CampaignWithReadinessResponse | null>(null);
  useEffect(() => {
    setLive(null);
  }, [data]);

  const current = live ?? data;
  const watch = useCampaignProgress(current?.campaign ?? null, setLive);

  const [filter, setFilter] = useState<RecipientFilter>(NO_RECIPIENT_FILTER);

  /*
   * THE RECIPIENT LIST IS ASKED FOR, AND EVERY ROUTE INTO IT STILL LANDS ON ROWS.
   *
   * Slice 25: closed, the list issues no request and adds none of its 341 nodes
   * to the page — it was 41% of this screen's scroll and was refetched on every
   * poll tick. `CampaignRecipients`'s header has the measurement.
   *
   * But four controls on this page exist *to put somebody in that list*, and a
   * link that focuses a collapsed section focuses nothing. `showRecipients`
   * is the one way to set the filter from outside the list, and it opens as it
   * narrows, so the two cannot be done separately by accident. A fifth link is
   * written by somebody who has not read this file; what they reach for is a
   * function called `showRecipients`, not a pair of calls they have to know to
   * make together.
   */
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  function showRecipients(next: RecipientFilter): void {
    setFilter(next);
    setRecipientsOpen(true);
  }

  if (current === null) {
    return (
      <AppLayout title="Campaign" actions={<BackToCampaigns />}>
        <Card>
          {loadingFirst ? (
            <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading this campaign…</span>
            </CardContent>
          ) : (
            <ErrorState error={error} title="Could not load this campaign" onRetry={reload} />
          )}
        </Card>
      </AppLayout>
    );
  }

  const { campaign, readiness } = current;
  const counts = campaign.progress.counts;
  // Recipients and results both follow the send: this changes whenever a
  // recipient is resolved — including by a delivery event landing well after
  // the send itself finished — and only then.
  const resultsRefreshKey = [
    counts.sent,
    counts.delivered,
    counts.bounced,
    counts.failed,
    counts.skipped,
  ].join(':');

  /**
   * The list-safety card follows the same movements **and the status**.
   *
   * A batch releasing, a campaign halting and a hold clearing are all status
   * changes that leave the delivery counts alone for a moment, so a key built
   * only from the counts would leave the wave list describing the batch before
   * last. Delivery events move both, which is what re-reads the figure the halt
   * gate is watching.
   */
  const listSafetyRefreshKey = [campaign.status, resultsRefreshKey].join('|');

  return (
    <AppLayout title={campaign.name} actions={<BackToCampaigns />}>
      <div className="flex flex-col gap-6">
        {error !== null && (
          <FormAlert tone="error">
            {errorMessage(error)} What is shown below is the last version that loaded.
          </FormAlert>
        )}

        {watch.error !== null && (
          <FormAlert
            tone="error"
            {...(isApiError(watch.error) && watch.error.requestId !== undefined
              ? { requestId: watch.error.requestId }
              : {})}
          >
            {errorMessage(watch.error)} These numbers have stopped updating — the send itself may
            still be running.
          </FormAlert>
        )}

        <CampaignHeader campaign={campaign} />

        {/*
         * SLICE 49 — ABOVE SCHEDULE, AND IT MOUNTS ONLY ON A WHATSAPP CAMPAIGN.
         *
         * What fills `{{1}}` is a decision taken *before* scheduling: an
         * unbound position is a blocker on the panel directly below this one,
         * and a reader who meets the refusal before the controls has to scroll
         * back up past the thing that refused them. It is the same ordering
         * argument slice 18 made for the list-safety card.
         *
         * `onSaved` re-reads rather than patching the campaign in place.
         * Bindings are half of `wa_bindings_incomplete`, and readiness is
         * computed by the server from the template's positions and the stored
         * bindings together — so a save that updated only the campaign would
         * leave Schedule disabled by a blocker that no longer exists, with
         * nothing on screen to explain it.
         */}
        <CampaignWhatsAppPersonalisation
          campaign={campaign}
          onRetry={reload}
          onSaved={() => {
            reload();
          }}
        />

        {campaign.status === 'draft' ? (
          <ScheduleCampaignPanel
            campaign={campaign}
            readiness={readiness}
            onScheduled={(response) => {
              setLive(response);
              // The recipient list now exists, and its waiting rows are exactly
              // the addresses that will actually receive this message — the
              // answer the send-guard panel could only give as a count, because
              // the allow-list itself never reaches the browser. Focusing the
              // list on them is the first thing somebody who has just scheduled
              // a campaign to a thousand contacts needs to see.
              showRecipients({ status: 'pending', skipReason: null });
            }}
          />
        ) : (
          <>
            {/* Above Progress, because a halted campaign is the only thing on
                this screen anybody has to act on, and a holding one is the only
                thing that explains why the counts have stopped moving. */}
            <CampaignListSafetyCard
              campaign={campaign}
              refreshKey={listSafetyRefreshKey}
              onResumed={(next) => {
                setLive({ campaign: next, readiness });
              }}
            />

            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle>Progress</CardTitle>
                  <WatchState watch={watch} campaign={campaign} onRefresh={reload} />
                </div>
              </CardHeader>
              <CardContent>
                <CampaignProgressPanel
                  progress={campaign.progress}
                  channel={campaign.channel}
                  onShowSkipReason={(reason) => {
                    showRecipients({ status: null, skipReason: reason });
                  }}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Who this reached</CardTitle>
                {/*
                  THE DESCRIPTION WENT — SLICE 28. It was a definition of a metric,
                  pre-empting a misreading: *"Reach is the distinct addresses in
                  the segment, never the size of the segment."* The figure's own
                  label says it (`Reachable by email`) and the segment line
                  directly below carries the contact count beside it, so a reader
                  who wants the comparison has both numbers in view. Nothing here
                  changed what a sender does.
                */}
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                <CampaignReachPanel reach={campaign.reach} sendGuard={readiness.sendGuard} />

                {/* Only while the campaign can still move. `sendGuard` is a live
                    evaluation — what the guard would do if this were scheduled
                    now — so it is written in the future tense, and on a finished
                    campaign the past-tense account is the skip breakdown above,
                    which carries the same reason with its own sentence. */}
                {!isCampaignTerminal(campaign.status) &&
                  campaign.progress.skippedByReason.not_on_send_allowlist > 0 && (
                    <SendGuardNotice
                      guard={readiness.sendGuard}
                      reach={campaign.reach}
                      action={
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            showRecipients({ status: null, skipReason: 'not_on_send_allowlist' });
                          }}
                        >
                          <ListFilter aria-hidden="true" className="size-4" />
                          Show who it held back
                        </Button>
                      }
                    />
                  )}
              </CardContent>
            </Card>

            <CampaignResultsPanel
              campaign={campaign}
              refreshKey={resultsRefreshKey}
              // The results are message-shaped and the skipped are people-shaped;
              // the panel says so in a line and this is the shortcut from that
              // line to the people themselves. Deliberately unfiltered by reason
              // — the reason-level links live in the skip breakdown above, and
              // the question this one answers is the blunter "which ones got
              // nothing?".
              onShowSkipped={() => {
                showRecipients({ status: 'skipped', skipReason: null });
              }}
            />

            {/* Slice 61: sent against held back, when the campaign held anyone back.
                Slice 65: and only on a workspace with Sense — the card asks the
                session itself, as the schedule step's Sense widgets do. */}
            <CampaignHoldoutCard campaignId={campaign.id} held={campaign.progress.skippedByReason.holdout} />
          </>
        )}

        <CampaignRecipients
          campaign={campaign}
          filter={filter}
          // The list is already open when its own chips and search are reachable,
          // so this one does not reveal — it only narrows.
          onFilterChange={setFilter}
          // The list follows the send: this changes whenever a recipient is
          // resolved, and only then.
          refreshKey={resultsRefreshKey}
          open={recipientsOpen}
          onOpenChange={setRecipientsOpen}
        />

        {!isCampaignTerminal(campaign.status) && (
          <CancelCampaign campaign={campaign} onCancelled={(next) => {
            setLive({ campaign: next, readiness });
          }} />
        )}
      </div>
    </AppLayout>
  );
}

function BackToCampaigns() {
  return (
    <Link to="/campaigns" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All campaigns
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * What this campaign is, in one card.
 *
 * The template's own status is on it, and that is not decoration: a campaign can
 * be built against a template that is later sent back to draft, and
 * `template_not_approved` then becomes a blocker somebody has to be able to see
 * the cause of. The scheduled time is rendered in the campaign's own timezone,
 * which is the whole reason the zone is stored beside the instant.
 */
function CampaignHeader({ campaign }: { campaign: Campaign }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <CampaignStatusBadge status={campaign.status} />
          {/* The surface's own label — the one string a screen prints for a
              surface (slice 44). A badge, so capitalised, where the channel
              word is lower case for the middle of a sentence. */}
          <Badge tone="neutral">{channelSurfaceLabel(campaign.channel)}</Badge>
          <Kicker as="span">
            created {formatDateTime(campaign.createdAt)} · updated{' '}
            {formatDateTime(campaign.updatedAt)}
          </Kicker>
        </div>

        <CardTitle>{campaign.name}</CardTitle>
        <CardDescription>{campaignSendingMeaning(campaign.status, campaign.channel)}</CardDescription>
      </CardHeader>

      <CardContent>
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Template">
            <span className="flex flex-wrap items-center gap-2">
              <Link
                to={`/templates/${campaign.template.id}`}
                className="text-text underline-offset-4 hover:underline"
              >
                {campaign.template.name}
              </Link>
              <TemplateStatusBadge status={campaign.template.status} />
            </span>
          </Fact>

          <Fact label="Segment">
            <Link
              to={`/segments/${campaign.segment.id}`}
              className="text-text underline-offset-4 hover:underline"
            >
              {campaign.segment.name}
            </Link>
          </Fact>

          <Fact label="From">
            {/*
              SLICE 49 — `fromEmail` IS NULL ON A WHATSAPP CAMPAIGN, AND NULL IS
              NOT AN EMPTY ADDRESS.

              0035 makes the column email-only in both directions, so a WhatsApp
              campaign carries no address at all rather than a leftover one.
              Printed raw that was a blank `dd` under a "From" label — a fact
              rendered as an absence, which reads as a campaign whose sender
              went missing. The sender is real and it is the number Meta
              verified; it is named here in the same mono style "no time yet"
              uses one column over, because it is a statement rather than a
              value the campaign chose.

              THE GATE IS THE CHANNEL, NOT THE NULL. It was `fromEmail === null`
              until slice 49's review, which is the same fact only while the new
              constraint holds — and a screen that assumes a constraint is a
              screen that lies the day it is violated. An email row that slipped
              through with no address read "your verified Amazon number", naming
              a provider that verifies numbers for a channel this campaign does
              not use, and hiding the violation behind a reassuring sentence.
              Asked by channel, the impossible row says what is actually true of
              it: there is no address on it.
            */}
            {campaign.channel === 'whatsapp' ? (
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                your verified {CHANNEL_PROVIDER_SHORT_LABEL[campaign.channel]} number
              </span>
            ) : campaign.fromEmail === null ? (
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                no address
              </span>
            ) : (
              <span className="break-all font-mono text-[0.8rem] text-muted">
                {campaign.fromName === null ? '' : `${campaign.fromName} · `}
                {campaign.fromEmail}
              </span>
            )}
          </Fact>

          <Fact label="Scheduled for">
            {campaign.scheduledAt === null ? (
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                no time yet
              </span>
            ) : (
              <span className="text-muted">
                {formatInTimeZone(campaign.scheduledAt, campaign.timezone)}
                <span className="ml-1.5 text-muted">{campaign.timezone}</span>
              </span>
            )}
          </Fact>
        </dl>
      </CardContent>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Kicker as="dt">{label}</Kicker>
      <dd className="min-w-0 text-[0.9rem]">{children}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The watch                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Whether the numbers on screen are being refreshed, said out loud.
 *
 * A screen that polls silently and a screen that has stopped polling look
 * identical, and the difference is whether the figures can be trusted a minute
 * from now. So the three states are named: watching, sleeping until a scheduled
 * campaign is nearly due, or not watching at all because there is nothing left
 * to watch.
 */
function WatchState({
  watch,
  campaign,
  onRefresh,
}: {
  watch: CampaignWatch;
  campaign: Campaign;
  onRefresh: () => void;
}) {
  if (watch.watching) {
    return (
      <span className="flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
        <Spinner size="sm" label={null} />
        <span role="status">updating live</span>
      </span>
    );
  }

  if (watch.sleepingUntil !== null) {
    return (
      <span className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
        watching from just before{' '}
        {formatInTimeZone(watch.sleepingUntil, campaign.timezone)}
      </span>
    );
  }

  return (
    <Button type="button" variant="ghost" size="sm" onClick={onRefresh}>
      <RotateCw aria-hidden="true" className="size-4" />
      Refresh
    </Button>
  );
}

/* -------------------------------------------------------------------------- */
/* Cancel                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Cancelling, with the confirmation inline rather than in a dialog.
 *
 * What the confirmation has to say is not "are you sure" but *what it can and
 * cannot undo*: it cannot un-send what Amazon has already accepted, and it will
 * not leave anybody pending. Everyone still waiting becomes a skipped recipient
 * carrying `campaign_cancelled`, so the progress numbers settle and the
 * recipient list can still say what happened to each person.
 *
 * A draft may be cancelled too, which `isCampaignCancellable` does not allow on
 * its own — it is the API route's rule rather than the shared one, because
 * `wend_app` holds no DELETE on `campaigns` and this is the only way to put an
 * abandoned draft out of the way.
 */
function CancelCampaign({
  campaign,
  onCancelled,
}: {
  campaign: Campaign;
  onCancelled: (campaign: Campaign) => void;
}) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );

  const isDraft = campaign.status === 'draft';
  if (!isDraft && !isCampaignCancellable(campaign.status)) return null;

  const waiting = campaign.progress.counts.pending;

  async function onCancel(): Promise<void> {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      const next = await cancelCampaign(campaign.id);
      toast({
        tone: 'success',
        title: 'Campaign cancelled',
        description:
          waiting === 0
            ? 'Nothing was waiting to go out.'
            : `${pluralise(waiting, 'recipient')} still waiting ${waiting === 1 ? 'was' : 'were'} skipped rather than left pending.`,
      });
      onCancelled(next);
      setConfirming(false);
      setPending(false);
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
      setPending(false);
    }
  }

  return (
    <Card className="border-bad/25">
      <CardHeader>
        <CardTitle className="text-[1rem]">
          {isDraft ? 'Cancel this draft' : 'Cancel this campaign'}
        </CardTitle>
        <CardDescription>
          {isDraft
            ? 'A draft has sent nothing, so cancelling puts it away. Campaigns are never deleted, and a cancelled one cannot be un-cancelled.'
            : `Stops whatever has not gone out yet. It cannot recall a message ${CHANNEL_PROVIDER_SHORT_LABEL[campaign.channel]} has already accepted, and a cancelled campaign cannot be un-cancelled.`}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failure !== null && (
          <FormAlert
            tone="error"
            {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
          >
            {failure.message}
          </FormAlert>
        )}

        {confirming ? (
          <div className="flex flex-col gap-3">
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              Cancel <span className="font-medium">{campaign.name}</span>?{' '}
              {waiting === 0
                ? 'Nothing is waiting to go out, so nothing changes except the status.'
                : `${pluralise(waiting, 'recipient')} still waiting will be skipped rather than left pending, each carrying “the campaign was cancelled before this message was sent”.`}{' '}
              This cannot be undone.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending}
                className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                onClick={() => {
                  void onCancel();
                }}
              >
                <Ban aria-hidden="true" className="size-4" />
                {pending ? 'Cancelling' : 'Yes, cancel it'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Leave it running
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Ban aria-hidden="true" className="size-4" />
              {isDraft ? 'Cancel draft' : 'Cancel campaign'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
